import { useCallback, useEffect, useState } from "react";
import { isEqual } from "lodash-es";
import { encodeQueryParams, objectToSearchString, useQueryParam, useQueryParams } from "use-query-params";
import { useUpdateEffect } from "@react-hookz/web";
import { getDurationStringSeconds, isAbsoluteTimeRange, isDurationString, isRelativeTimeRange } from "@chronosphereio/core";
import { throwDevError } from "../../../core/utils/logging/index.js";
import { DEFAULT_TIME_RANGE, isValidDate, useDeepMemo } from "../../../core/index.js";
const TIME_RANGE_URL_PARAMS = {
    start: 'start',
    end: 'end'
};
function getEncodedValue(input, allowEmptyString) {
    if (null == input) return input;
    if (0 === input.length && (!allowEmptyString || allowEmptyString && '' !== input)) return null;
    const str = input instanceof Array ? input[0] : input;
    if (null == str) return str;
    if (!allowEmptyString && '' === str) return null;
    return str;
}
function encodeTimeRangeValue(timeOptionValue) {
    if (!timeOptionValue) return timeOptionValue;
    if ('string' == typeof timeOptionValue) {
        if (isDurationString(timeOptionValue)) return timeOptionValue;
    }
    return timeOptionValue.getTime().toString();
}
const TIMESTAMP_REGEX = /\d{13}/;
function decodeTimeRangeValue(input) {
    const paramString = getEncodedValue(input);
    if (null == paramString) return paramString;
    if (isDurationString(paramString)) return paramString;
    if ('string' == typeof paramString) {
        const timestamp = paramString.match(TIMESTAMP_REGEX);
        if (!timestamp) return;
        const timestampDate = new Date(Number(timestamp[0]));
        if (isValidDate(timestampDate)) return timestampDate;
    }
}
const TimeRangeParam = {
    encode: encodeTimeRangeValue,
    decode: decodeTimeRangeValue,
    equals: (valueA, valueB)=>{
        if (valueA === valueB) return true;
        if (null == valueA || null == valueB) return valueA === valueB;
        return valueA.valueOf() === valueB.valueOf();
    }
};
const timeRangeQueryConfig = {
    start: TimeRangeParam,
    end: TimeRangeParam
};
const refreshIntervalQueryConfig = {
    refresh: TimeRangeParam
};
function useInitialTimeRange(defaultTimeRange = DEFAULT_TIME_RANGE) {
    const [query] = useQueryParams(timeRangeQueryConfig, {
        updateType: 'replaceIn'
    });
    const { start, end } = query;
    return useDeepMemo(()=>{
        let initialTimeRange = defaultTimeRange;
        if (!start) return initialTimeRange;
        const startStr = start.toString();
        if (isDurationString(startStr)) initialTimeRange = {
            pastDuration: startStr
        };
        else if ('string' != typeof start && isValidDate(start) && end && 'string' != typeof end && isValidDate(end)) initialTimeRange = {
            start: start,
            end: end
        };
        return initialTimeRange;
    }, [
        start,
        end,
        defaultTimeRange
    ]);
}
function useInternalTimeRange({ initialTimeRange, enabledURLParams = true, recomputeKey }) {
    const [query, setQuery] = useQueryParams(timeRangeQueryConfig, {
        updateType: 'pushIn'
    });
    const [paramsLoaded, setParamsLoaded] = useState(false);
    const [timeRangeState, setTimeRangeState] = useState(initialTimeRange);
    useUpdateEffect(()=>{
        if (!enabledURLParams && void 0 !== recomputeKey && !isEqual(initialTimeRange, timeRangeState)) setTimeRangeState(initialTimeRange);
    }, [
        recomputeKey
    ]);
    const { start, end } = query;
    useEffect(()=>{
        if (enabledURLParams && !paramsLoaded && !start) {
            if (isRelativeTimeRange(initialTimeRange)) {
                setQuery({
                    start: initialTimeRange.pastDuration,
                    end: void 0
                }, 'replaceIn');
                setParamsLoaded(true);
            }
            if (isAbsoluteTimeRange(initialTimeRange)) {
                setQuery(initialTimeRange, 'replaceIn');
                setParamsLoaded(true);
            }
        }
    }, [
        initialTimeRange,
        enabledURLParams,
        paramsLoaded,
        start,
        setQuery
    ]);
    const setTimeRangeQueryParams = useCallback((value, updateType)=>{
        if (isRelativeTimeRange(value)) setQuery({
            start: value.pastDuration,
            end: void 0
        }, updateType);
        else {
            if (!isValidDate(value.start) || !isValidDate(value.end)) return void throwDevError('setTimeRangeQueryParams called with invalid dates');
            setQuery({
                start: value.start,
                end: value.end
            }, updateType);
        }
    }, [
        setQuery
    ]);
    return useDeepMemo(()=>{
        if (!enabledURLParams) return {
            timeRange: timeRangeState,
            setTimeRange: setTimeRangeState
        };
        let timeRange = initialTimeRange;
        if (!start) return {
            timeRange,
            setTimeRange: setTimeRangeQueryParams
        };
        const startStr = start.toString();
        if (isDurationString(startStr)) timeRange = {
            pastDuration: startStr
        };
        else if ('string' != typeof start && isValidDate(start) && end && 'string' != typeof end && isValidDate(end)) timeRange = {
            start: start,
            end: end
        };
        return {
            timeRange,
            setTimeRange: setTimeRangeQueryParams
        };
    }, [
        initialTimeRange,
        start,
        end,
        enabledURLParams,
        timeRangeState
    ]);
}
const DurationStringParam = {
    encode: (durationString)=>durationString,
    decode: (arrayStr)=>{
        if ('string' == typeof arrayStr) return arrayStr;
        return null;
    }
};
const MIN_REFRESH_INTERVAL_SECONDS = 30;
function isValidRefreshInterval(refreshInterval) {
    try {
        const refreshIntervalSeconds = getDurationStringSeconds(refreshInterval);
        if (void 0 === refreshIntervalSeconds) return false;
        return refreshIntervalSeconds >= MIN_REFRESH_INTERVAL_SECONDS;
    } catch  {
        return false;
    }
}
function useSetRefreshIntervalParams(args) {
    const { initialRefreshInterval = null, enabledURLParams } = args;
    const [refreshIntervalQuery, setRefreshIntervalQuery] = useQueryParam('refresh', DurationStringParam);
    const [refreshIntervalState, setRefreshIntervalState] = useState(initialRefreshInterval);
    if (null !== refreshIntervalQuery && !isValidRefreshInterval(refreshIntervalQuery)) setRefreshIntervalQuery(`${MIN_REFRESH_INTERVAL_SECONDS}s`);
    if (!enabledURLParams) return {
        refreshInterval: refreshIntervalState,
        setRefreshInterval: setRefreshIntervalState
    };
    return {
        refreshInterval: refreshIntervalQuery,
        setRefreshInterval: setRefreshIntervalQuery
    };
}
function timeRangeToQueryString(timeRangeValue) {
    if (isRelativeTimeRange(timeRangeValue)) {
        const encoded = encodeQueryParams(timeRangeQueryConfig, {
            start: timeRangeValue.pastDuration,
            end: timeRangeValue.end
        });
        return objectToSearchString(encoded);
    }
    const encoded = encodeQueryParams(timeRangeQueryConfig, {
        start: timeRangeValue.start,
        end: timeRangeValue.end
    });
    return objectToSearchString(encoded);
}
export { TIME_RANGE_URL_PARAMS, TimeRangeParam, decodeTimeRangeValue, encodeTimeRangeValue, isValidRefreshInterval, refreshIntervalQueryConfig, timeRangeQueryConfig, timeRangeToQueryString, useInitialTimeRange, useInternalTimeRange, useSetRefreshIntervalParams };

//# sourceMappingURL=query-params.js.map